import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useSEO } from "@/hooks/useSEO";
import Navbar from "@/components/Navbar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PersonalSafetyRadar from "@/components/PersonalSafetyRadar";
import SmartAlerts from "@/components/SmartAlerts";
import FlightRiskIndex from "@/components/FlightRiskIndex";
import SafeRoutes from "@/components/SafeRoutes";
import CountryEmergencyGuide from "@/components/CountryEmergencyGuide";
import MissileEarlyWarning from "@/components/MissileEarlyWarning";
import PanicAlerts from "@/components/PanicAlerts";
import { Shield, Bell, Plane, Route, BookOpen, Target, Siren, Crown, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import PricingDialog from "@/components/PricingDialog";
import { useLocalCurrency } from "@/hooks/useLocalCurrency";

const tabs = [
  { value: "radar", label: "Safety Radar", icon: Shield },
  { value: "alerts", label: "Smart Alerts", icon: Bell },
  { value: "flights", label: "Flight Risk", icon: Plane },
  { value: "routes", label: "Safe Routes", icon: Route },
  { value: "guide", label: "Emergency Guide", icon: BookOpen },
  { value: "missile", label: "Missile Warning", icon: Target },
  { value: "panic", label: "Panic Alert", icon: Siren },
];

const PremiumDashboard = () => {
  const { user, isPremium, loading } = useAuth();
  const navigate = useNavigate();
  const { formatPriceWithEur } = useLocalCurrency();
  const [pricingOpen, setPricingOpen] = useState(false);

  useSEO({
    title: "Premium Dashboard | Global Conflict Monitor",
    description: "Your unified premium control center: Safety Radar, Smart Alerts, Flight Risk, Safe Routes, Emergency Guides, Missile Warning and Panic Alerts in one place.",
    path: "/dashboard",
    noindex: true,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="text-muted-foreground animate-pulse">Loading...</div>
        </div>
      </div>
    );
  }

  if (!user || !isPremium) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <Crown className="mx-auto mb-4 h-16 w-16 text-primary" />
          <h1 className="mb-2 text-3xl font-bold text-foreground">Premium Dashboard</h1>
          <p className="mb-8 text-muted-foreground max-w-md mx-auto">
            Access all premium SaaS tools in one unified dashboard. Upgrade to unlock Safety Radar, Smart Alerts, Flight Risk Index, and more.
          </p>
          <Button
            size="lg"
            onClick={() => setPricingOpen(true)}
          >
            <Zap className="mr-2 h-5 w-5" /> Upgrade to Premium — {formatPriceWithEur(2.99)}/mo
          </Button>
        </div>
        <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 py-6">
        <div className="mb-6 flex items-center gap-3">
          <Crown className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Premium Dashboard</h1>
            <p className="text-sm text-muted-foreground">All your security tools in one place</p>
          </div>
        </div>

        <Tabs defaultValue="radar" className="w-full">
          <TabsList className="mb-6 flex h-auto flex-wrap gap-1 bg-transparent p-0">
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="gap-1.5 rounded-lg border border-border bg-card px-3 py-2 text-xs font-medium data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
              >
                <tab.icon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sr-only sm:hidden">{tab.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="radar"><PersonalSafetyRadar embedded /></TabsContent>
          <TabsContent value="alerts"><SmartAlerts embedded /></TabsContent>
          <TabsContent value="flights"><FlightRiskIndex embedded /></TabsContent>
          <TabsContent value="routes"><SafeRoutes embedded /></TabsContent>
          <TabsContent value="guide"><CountryEmergencyGuide embedded /></TabsContent>
          <TabsContent value="missile"><MissileEarlyWarning embedded /></TabsContent>
          <TabsContent value="panic"><PanicAlerts embedded /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PremiumDashboard;
